import React, { useEffect, useState } from "react";
import { api, formatErr } from "../../lib/api";
import { toast } from "sonner";
import { Lock, Plus, Loader2, Copy, Trash2, Check, KeyRound } from "lucide-react";

const fmtDate = (s) => {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString("en-GB", { day: "numeric", month: "short" }); }
  catch { return s; }
};

/**
 * Admin card for the optional /beta access-code gate.
 * Toggle the gate on/off, generate codes, copy share-links, revoke codes.
 */
export default function BetaCodesCard() {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [codes, setCodes] = useState([]);
  const [creating, setCreating] = useState(false);
  const [label, setLabel] = useState("");
  const [maxUses, setMaxUses] = useState("");
  const [copiedCode, setCopiedCode] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        api.get("/settings/public"),
        api.get("/admin/beta-codes"),
      ]);
      setEnabled(!!s.data.beta_gate_enabled);
      setCodes(c.data.items || []);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const toggle = async () => {
    const next = !enabled;
    if (next && !confirm("Turn the beta gate ON? Anyone without an access code or admin login will be redirected to /beta. You will not be locked out — admins always bypass the gate.")) return;
    if (!next && !confirm("Turn the gate OFF? The site becomes publicly accessible to everyone.")) return;
    try {
      await api.post(`/admin/settings/beta-gate?enabled=${next}`);
      setEnabled(next);
      toast.success(next ? "Beta gate is now ON." : "Beta gate is now OFF — site is public.");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const create = async () => {
    setCreating(true);
    try {
      const payload = { label: label || null };
      if (maxUses) payload.max_uses = Number(maxUses);
      const { data } = await api.post("/admin/beta-codes", payload);
      setCodes((prev) => [data.code_doc, ...prev]);
      toast.success(`Code ${data.code_doc.code} created`);
      setLabel(""); setMaxUses("");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setCreating(false); }
  };

  const copyLink = async (code) => {
    const url = `${window.location.origin}/beta?code=${code}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedCode(code);
      toast.success("Invite link copied");
      setTimeout(() => setCopiedCode(null), 2500);
    } catch { toast.error("Couldn't copy"); }
  };

  const revoke = async (code) => {
    if (!confirm(`Revoke code ${code}? Anyone trying to redeem it will be turned away.`)) return;
    try {
      await api.delete(`/admin/beta-codes/${code}`);
      setCodes((prev) => prev.filter((c) => c.code !== code));
      toast.success("Code revoked");
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="border hairline bg-white p-6 mb-8" data-testid="beta-codes-card">
      <header className="flex items-end justify-between gap-3 mb-5 flex-wrap">
        <div>
          <div className="eyebrow text-[#ff4500] flex items-center gap-1.5">
            <Lock className="w-3 h-3" /> Beta access gate
          </div>
          <h3 className="font-display text-xl mt-1">Invite-only access codes</h3>
          <p className="text-xs text-[#666] mt-1 max-w-xl">
            When the gate is ON, anyone without an admin login OR a redeemed code is sent to <code className="bg-[#fafafa] px-1">/beta</code>. Use this to soft-launch with friends before public marketing. Admins always bypass the gate.
          </p>
        </div>
        <button
          onClick={toggle}
          className={`inline-flex items-center gap-2 text-xs mono uppercase tracking-wider px-4 py-2 border-2 transition-colors ${enabled ? "bg-[#ff4500] text-white border-[#ff4500]" : "bg-white border-[#ddd] hover:border-[#ff4500]"}`}
          data-testid="beta-gate-toggle"
        >
          <span className={`w-2 h-2 rounded-full ${enabled ? "bg-white" : "bg-[#aaa]"}`} />
          Gate {enabled ? "ON" : "OFF"}
        </button>
      </header>

      {/* Create new code */}
      <div className="bg-[#fafafa] border hairline p-4 mb-4 grid grid-cols-1 sm:grid-cols-[1fr_120px_auto] gap-3 items-end">
        <div>
          <label className="eyebrow block mb-1.5">Label (optional)</label>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value.slice(0, 60))}
            placeholder="e.g. Sarah's WhatsApp invite"
            className="w-full border hairline px-3 py-2 text-sm"
            data-testid="beta-code-label"
          />
        </div>
        <div>
          <label className="eyebrow block mb-1.5">Max uses</label>
          <input
            type="number"
            min="1"
            value={maxUses}
            onChange={(e) => setMaxUses(e.target.value.replace(/[^0-9]/g, "").slice(0, 4))}
            placeholder="∞"
            className="w-full border hairline px-3 py-2 text-sm mono"
            data-testid="beta-code-max-uses"
          />
        </div>
        <button
          onClick={create}
          disabled={creating}
          className="btn-primary text-xs px-5 py-2.5 inline-flex items-center gap-2 disabled:opacity-60"
          data-testid="beta-code-create"
        >
          {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" strokeWidth={2.5} />}
          Generate
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-6 text-[#888] gap-2 text-sm mono">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading codes…
        </div>
      ) : codes.length === 0 ? (
        <div className="text-center py-6 text-sm text-[#666] bg-[#fafafa] border hairline">
          <KeyRound className="w-6 h-6 text-[#ff4500]/40 mx-auto mb-2" strokeWidth={1.5} />
          No codes yet. Generate one above to start inviting friends.
        </div>
      ) : (
        <div className="border hairline" data-testid="beta-codes-list">
          {codes.map((c) => {
            const used = c.used_count || 0;
            const cap = c.max_uses;
            const exhausted = cap != null && used >= cap;
            return (
              <div key={c.code} className="grid grid-cols-[auto_1fr_auto_auto] sm:grid-cols-[160px_1fr_120px_auto_auto] items-center gap-3 px-3 py-2.5 border-b hairline last:border-b-0 hover:bg-[#fffdfa]" data-testid={`beta-code-${c.code}`}>
                <code className={`mono text-sm sm:text-base px-2 py-1 border hairline tracking-[0.15em] ${exhausted ? "bg-[#fafafa] text-[#888] line-through" : "bg-[#fff4ee]"}`}>
                  {c.code}
                </code>
                <div className="text-xs leading-tight min-w-0">
                  <div className="truncate text-[#222]">{c.label || <span className="text-[#888] italic">No label</span>}</div>
                  <div className="text-[10px] mono text-[#888] mt-0.5">Created {fmtDate(c.created_at)}</div>
                </div>
                <div className="text-[11px] mono text-right">
                  <span className={exhausted ? "text-[#dc2626]" : "text-[#444]"}>
                    {used}{cap != null ? ` / ${cap}` : ""}
                  </span>
                  <span className="block text-[10px] text-[#888]">used</span>
                </div>
                <button
                  onClick={() => copyLink(c.code)}
                  className="p-2 hover:bg-[#fff4ee] border hairline"
                  aria-label="Copy invite link"
                  title="Copy invite link"
                  data-testid={`beta-code-copy-${c.code}`}
                >
                  {copiedCode === c.code ? (
                    <Check className="w-3.5 h-3.5 text-[#22c55e]" strokeWidth={2.5} />
                  ) : (
                    <Copy className="w-3.5 h-3.5" strokeWidth={2} />
                  )}
                </button>
                <button
                  onClick={() => revoke(c.code)}
                  className="p-2 hover:bg-[#fee2e2] hover:text-[#dc2626] border hairline"
                  aria-label="Revoke code"
                  title="Revoke code"
                  data-testid={`beta-code-revoke-${c.code}`}
                >
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={2} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
